import type { AiTextTransformProps } from '@ai-chat-widget-contracts/contracts'

type BridgeProps = Omit<AiTextTransformProps, 'runtime'>

interface TransformRegistration {
  getProps: () => BridgeProps
  setActive: (active: boolean, signal: number) => void
}

const registrations = new Map<symbol, TransformRegistration>()
let activationSequence = 0
let listenersInstalled = false

export const hasValidTextSelection = (props: BridgeProps, eventTarget: EventTarget | null) => {
  const input = props.inputRef?.current
  if (input) {
    if (eventTarget !== input && document.activeElement !== input) return false
    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? 0
    return start !== end && input.value.slice(start, end).trim().length > 0
  }

  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.toString().trim()) return false
  return Boolean(props.containerRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer))
}

const routeSelection = (event: Event) => {
  const selected = Array.from(registrations.entries()).find(([, registration]) => {
    const props = registration.getProps()
    return props.enabled && hasValidTextSelection(props, event.target)
  })
  if (!selected) return

  activationSequence += 1
  registrations.forEach((registration, id) => {
    registration.setActive(id === selected[0], activationSequence)
  })
}

const installController = () => {
  if (listenersInstalled || typeof document === 'undefined') return
  document.addEventListener('selectionchange', routeSelection)
  document.addEventListener('mouseup', routeSelection)
  document.addEventListener('keyup', routeSelection)
  listenersInstalled = true
}

const uninstallController = () => {
  if (!listenersInstalled || registrations.size > 0 || typeof document === 'undefined') return
  document.removeEventListener('selectionchange', routeSelection)
  document.removeEventListener('mouseup', routeSelection)
  document.removeEventListener('keyup', routeSelection)
  listenersInstalled = false
}

export const registerTextTransformTarget = (
  getProps: () => BridgeProps,
  setActive: TransformRegistration['setActive'],
) => {
  const id = Symbol('ai-text-transform')
  registrations.set(id, { getProps, setActive })
  installController()
  return () => {
    registrations.delete(id)
    uninstallController()
  }
}
